import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import Button from 'react-bootstrap/Button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import moment from 'moment';
import {
  AcceptRejectMeetingInvitationAsync,
  deleteEvent,
  getAllEmployees,
  getAllEvents,
} from '../../../../services/calendarService/EventService';
import { useAuth } from '../../../../firebase/AuthProvider';
import { AllEvent, AllUsers } from '../../../../interfaces/identity/calendar/Events';
import Modal from './Model';
import Loader from '../../loader/Loader';
import EditEvent from './EditEvent';
import threedot from '../../../../assets/images/threedot.svg';
import videocall from '../../../../assets/images/videocall.svg';
import editicons from '../../../../assets/images/mage_edit.svg';
import CalendarIcon from '../../../../assets/images/DATE.png';
import DescriptionIcon from '../../../../assets/images/fluent_text.png';
import LinkIcon from '../../../../assets/images/tabler_link.png';
import trashicons from '../../../../assets/images/delete-rounded.svg';
import { GetExistingOrCretateMeetingAsync } from '../../../../services/videoService/VideoService';
import { LobbyState } from '../../../../interfaces/videocallDtos/videoCall';
import { useNavigate } from 'react-router-dom';
import { showConfirmationDialog } from '../../../modal/ConfimationModal/Confimation';
import { formatRemainingTimeForMeeting } from '../../../../utilities/formatRemainingTimeForMeeting';
import ScopeModal from './ScopeModal'; // Import the new ScopeModal
import ModalMeet from '../ModalMeet';
import TimeIcon from '../../../../assets/images/ClockCal.svg';
import RescheduleMeetingModal from '../../tasks/Modals/RescheduleMeetingModal';
import RescheduleIcon from '../../../../assets/images/Reschedule.svg';
import UserAvatar from '../UserAvatar';


interface EventData {
  title: string;
  startTime: string;
  endTime: string;
  description: string;
  location: string;
  meetingDetails: string;
  ownerUserID: string;
  participantUserIds: string[];
  externalUserEmails: string[];
  taskId: number;
  taskTitle: string;
  online: boolean;
  isSprout: boolean;
  recurrenceRule?: string; // Add recurrenceRule
  parentEventId?: string; // Add parentEventId
  time_zone?: string; // Add time_zone
}

interface ViewEventProps {
  event: AllEvent | null;
  onClose: () => void;
  refreshAllEvents?: () => void;
}

export default function ViewEvent({ event, onClose, refreshAllEvents }: ViewEventProps) {
  const { currentUser, currentUserInfo } = useAuth();
  const token = (currentUser as any)?.accessToken;
  const navigate = useNavigate();
  const modalContentRef = useRef<HTMLDivElement>(null); // Ref for modal content
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteScopeModal, setShowDeleteScopeModal] = useState(false); // New state for delete scope modal
  const [employees, setEmployees] = useState<AllUsers[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<AllEvent[]>([]);
  const [showActions, setShowActions] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false); // Add this state
  const [formData, setFormData] = useState<EventData>({
    title: '',
    startTime: '',
    endTime: '',
    description: '',
    location: '',
    meetingDetails: '',
    ownerUserID: '',
    participantUserIds: [],
    externalUserEmails: [],
    taskId: 0,
    taskTitle: '',
    online: true,
    isSprout: true,
  });

  const [currentTime, setCurrentTime] = useState(moment());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(moment()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Close on Esc key
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  useEffect(() => {
    // Close on click outside
    const handleClickOutside = (event: MouseEvent) => {
      if (showDeleteScopeModal || isEditModalOpen) {
        return;
      }
      if (modalContentRef.current && !modalContentRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, showDeleteScopeModal, isEditModalOpen]);

  async function fetchAllEmployee() {
    if (!currentUser) return;
    setLoading(true);
    try {
      const response = await getAllEmployees(token);
      if (response) setEmployees(response);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchAllEvents() {
    if (!currentUser) return;
    setLoading(true);
    try {
      const start = moment().subtract(1, 'year').toDate();
      const end = moment().add(1, 'year').toDate();
      const response = await getAllEvents(token, start, end);
      if (response) {
        setLoading(false);
        setCalendarEvents(response);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleDelete = async () => {
    if (!event || !token) return;

    if (event.recurrenceRule || event.parentEventId) {
      setShowDeleteScopeModal(true);
    } else {
      // For single events, confirm directly
      const isConfirmed = await showConfirmationDialog();
      if (isConfirmed) {
        await executeDelete('single'); // Default to 'single' for non-recurring
      }
    }
  };

  const executeDelete = async (scope: 'single' | 'future') => {
    if (!event || !token) return;
    setLoading(true);
    try {
      const success = await deleteEvent(event.id, token, scope);
      if (success) {
        toast.success('Event deleted successfully!');
        fetchAllEvents();
        onClose();
        if (refreshAllEvents) refreshAllEvents();
      } else {
        toast.error('Failed to delete the event.');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('An error occurred.');
    } finally {
      setLoading(false);
      setShowDeleteScopeModal(false);
      setShowConfirmModal(false);
    }
  };

  const onEditOpenPopup = () => setEditModalOpen(true);

  // Add this function for opening the reschedule modal
  const onRescheduleOpenPopup = () => {
    setShowRescheduleModal(true);
    setShowActions(false); // Close the actions dropdown
  };

  useEffect(() => {
    fetchAllEmployee();
    fetchAllEvents();
  }, []);

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || '',
        startTime: event.startTime, // store ISO string
        endTime: event.endTime,     // store ISO string
        description: event.description || '',
        location: event.location,
        meetingDetails: event.meetingDetails,
        ownerUserID: event.ownerUserID || '',
        participantUserIds: event.participantUserIds || [],
        externalUserEmails: event.externalUserEmails || [],
        taskId: event.taskId || 0,
        taskTitle: event.taskTitle,
        online: event.online,
        isSprout: event.isSprout,
        recurrenceRule: event.recurrenceRule || undefined, // Populate recurrenceRule
        parentEventId: event.parentEventId || undefined, // Populate parentEventId
        time_zone: event.time_zone || undefined, // Populate time_zone
      });
    }
  }, [event]);

  const participants = formData.participantUserIds.map((id) => {
    const user = employees.find((emp) => emp.userId === id);

    return {
      userId: id,
      name:
        user?.userName ||
        (currentUser?.uid === id
          ? `${currentUserInfo?.firstName} ${currentUserInfo?.lastName}`
          : id),
    };
  });

  const handleClickJoin = (eventId: string, disabled: boolean) => {
    if (disabled) {
      toast.info('Joining opens 30 min before start.');
      return;
    }

    setLoading(true);
    GetExistingOrCretateMeetingAsync(token, eventId)
      .then((res) => {
        if (res.success) {
          const states: LobbyState = {
            calenderEventId: eventId,
            isPublisher: res.data.isPublisher,
            isOneToOneCall: false,
          };
          navigate(`/lobby/${res.data.roomId}`, { state: states });
        } else {
          toast.error(res.message);
        }
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const AcceptRejectMeetingInvitation = async (isInvitationAccept: boolean) => {
    if (!currentUser || !event?.id) return;
    const isConfirm = await showConfirmationDialog();
    if (!isConfirm) return;
    setLoading(true);
    AcceptRejectMeetingInvitationAsync(currentUser?.uid, event?.id, isInvitationAccept, token)
      .then((res) => {
        if (res.success) {
          if (refreshAllEvents) refreshAllEvents();
          toast.success(res.message);
          onClose();
        } else {
          toast.error(res.message);
        }
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  if (!event) return null;

  // ---- Pre-join logic ----
  const start = moment.utc(event.startTime).local();
  const end = moment.utc(event.endTime).local();
  const preJoinStart = start.clone().subtract(30, 'minutes');

  const isWithinPrejoinWindow =
    currentTime.isSameOrAfter(preJoinStart) && currentTime.isSameOrBefore(end);
  const isBeforePrejoin = currentTime.isBefore(preJoinStart);
  const canJoin = isWithinPrejoinWindow;

  const msUntilPrejoin = preJoinStart.diff(currentTime);
  let tooltipMessage = 'Joining opens 30 min before start.';
  if (msUntilPrejoin > 0 && msUntilPrejoin <= 60 * 60 * 1000) {
    tooltipMessage = `You can join in ${formatRemainingTimeForMeeting(msUntilPrejoin)}`;
  } else if (msUntilPrejoin <= 0 && !isWithinPrejoinWindow) {
    tooltipMessage = 'Meeting already ended.';
  }


  const MAX_CHARS = 140;
  const description = formData.description || "--";

  const isLongText = description.length > MAX_CHARS;
  const displayedText =
    !isExpanded && isLongText
      ? description.slice(0, MAX_CHARS) + "..."
      : description;


  const formatDate = (dateStr: string) => {
    return moment.utc(dateStr).local().format('dddd, MMM DD');
    // e.g., "Wednesday, Dec 01"
  };

  const getInitials = (name: string) => {
    if (!name) return "?";

    const clean = name.trim().replace(/\s+/g, " ");
    const words = clean.split(" ");

    // ID / random string → only first letter
    if (words.length === 1) {
      return clean.charAt(0).toUpperCase();
    }

    // Normal name → first letter of first two words
    return (words[0][0] + words[1][0]).toUpperCase();
  };


  const colors = [
    "bg-red-500",
    "bg-green-500",
    "bg-blue-500",
    "bg-purple-500",
    "bg-yellow-500",
    "bg-pink-500",
    "bg-indigo-500",
  ];

  const getColor = (name: string) =>
    colors[name.charCodeAt(0) % colors.length];

  const Avatar = ({ name }: { name: string }) => {
    const [show, setShow] = React.useState(false);

    return (
      <div
        className="relative"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        <div
          className={`h-8 w-8 rounded-full border-2 border-white 
        ${getColor(name)} flex items-center justify-center 
        text-xs font-semibold text-white`}
        >
          {getInitials(name)}
        </div>

        {show && (
          <div
            className="absolute z-[9999] -top-9 left-[100%] 
          -translate-x-1/2 whitespace-nowrap rounded 
          bg-white px-2 py-1 text-xs text-black truncate max-w-24 shadow-md"
          >
            {name}
          </div>
        )}
      </div>
    );
  };

  const formatTime = (dateStr: string) => {
    return moment.utc(dateStr).local().format('hh:mm A');
  };

  return (
    <>
      {loading && <Loader />}
      <div className="overflow-hidden p-[20px] bg-white dark:bg-gray-800 rounded-xl" ref={modalContentRef}>
        <div className="mb-6 flex justify-between ">
          <div className="top_bar_modal_head">
            <p className="!text-lg !font-medium !text-[#7E57C2] capitalize truncate">{formData.title}</p>
            {/* <p className="text-sm text-gray-500 dark:text-gray-400">
              {moment.utc(event.startTime).local().format('dddd, MMMM DD, YYYY')}
            </p> */}
          </div>
          {event?.ownerUserID === currentUser?.uid &&
            currentTime.isBefore(moment.utc(event?.startTime).local()) && (
              <div style={{ position: 'relative' }}>
                <img
                  src={threedot}
                  alt="threedot"
                  onClick={() => setShowActions(!showActions)}
                  style={{ cursor: 'pointer' }}
                />
                {showActions && (
                  <div className="dots_dropdown !w-36 p-0 shadow-lg absolute right-0 mt-2 bg-white rounded-md overflow-hidden z-10" style={{ minWidth: '120px' }}>
                    <div
                      className="dots_dropdown_flx px-3 py-2 w-full text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-2"
                      onClick={onRescheduleOpenPopup}
                    >
                      <img src={RescheduleIcon} alt="Reschedule" className="w-4 h-4" />
                      <span>Reschedule</span>
                    </div>
                    <div className="dots_dropdown_flx px-3 py-2 w-full text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-2" onClick={onEditOpenPopup}>
                      <img src={editicons} alt="Edit" className="w-4 h-4" />
                      <span>Edit</span>
                    </div>
                    {/* Add the Reschedule button here */}
                    <div
                      className="dots_dropdown_flx px-3 py-2 w-full text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-2"
                      onClick={handleDelete}
                    >
                      <img src={trashicons} alt="Delete" className="w-4 h-4" />
                      <span>Delete</span>
                    </div>
                  </div>
                )}
              </div>
            )}
        </div>

        {/* {formData.description && (
          <div className="mb-4">
            <label className="text-sm font-semibold text-gray-600 dark:text-gray-400 block mb-1">Description</label>
            <p className="text-gray-800 dark:text-gray-200">{formData.description}</p>
          </div>
        )} */}

        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="stats_filled">
            <label>Task</label>
            <p>{formData.taskTitle || '--'}</p>
          </div>
          <div className="stats_filled">
            <label>Time</label>
            <p>{formData.startTime} - {formData.endTime}</p>
          </div>
          <div className="stats_filled">
            <label>Attendees</label>
            <p>{participantNames.join(', ') || '--'}</p>
          </div>
          <div className="stats_filled">
            <label>External User</label>
            <p>{formData.externalUserEmails.length > 0 ? formData.externalUserEmails.join(', ') : '--'}</p>
          </div>
          <div className="stats_filled">
            <label>Meeting Creator</label>
            <p>{event?.ownerName || '--'}</p>
          </div>

          {!formData.online ? (
            <div className="stats_filled">
              <label>Location</label>
              <p>{formData.location}</p>
            </div>
          ) : (
            <div className="stats_filled">
              <label>Meeting Link</label>
              {formData.meetingDetails && formData.meetingDetails.length > 1 ? (
                <p>
                  <a href={formData.meetingDetails} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline break-all" title={formData.meetingDetails}>
                    {formData.meetingDetails}
                  </a>
                </p>
              ) : (
                <p>--</p>
              )}
            </div>
          )}
        </div> */}

        {/* <div className="flex items-center gap-3">
          <img src={CalendarIcon} alt="calendar" className="w-6 h-6" />
          <span className="text-[#676767] !text-base">{formatDate(formData?.startTime)}</span>
        </div> */}
        <div className='flex justify-between flex-wrap'>
          <div className="flex items-center gap-3">
            <img src={CalendarIcon} alt="calendar" className="w-6 h-6" />
            <span className="text-[#676767] !text-base">
              {formatDate(formData.startTime)}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <img src={TimeIcon} alt="time" className="w-5 h-5 opacity-75" />
            <span className="text-[#676767] !text-base">
              {formatTime(formData.startTime)}
            </span>
          </div>
        </div>
        <div
          style={{ alignItems: "start" }}
          className="flex gap-3 mt-3">
          <img
            src={DescriptionIcon}
            alt="description"
            className="w-6 h-6 mt-1"
          />

          <div className="text-[#676767] text-base leading-relaxed text-long">
            <span>{displayedText}</span>

            {isLongText && (
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="ml-1 text-[#7E57C2] font-medium hover:underline text-sm"
              >
                {isExpanded ? "Read less" : "Read more"}
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 mt-3 !text-base">
          <img src={LinkIcon} alt="link" className="w-6 h-6" />
          {formData.location && formData.location.length > 0 ? (
            <p className="m-0">
              {formData.location}
            </p>
          ) : formData.isSprout ? (
            <p className="text-[#676767] break-all m-0"
              title="Join via Waivera">
              Join via Waivera
            </p>
          ) : (
            <p className="m-0">--</p>
          )}
        </div>
        {/* <div className="flex items-center mt-3 !text-base">
          <div className="flex items-center">
            <div className="h-7 w-7 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center">
              <span className="text-xs text-gray-500">
                {participantNames.length > 0 ? participantNames.length : "-"}
              </span>
            </div>
          </div>

          <span className="text-[#4A5565] text-sm md:text-base">
            {participantNames.length > 0 ? `- Attendees` : "No attendees"}
          </span>
        </div> */}
        <div className="flex items-center mt-3">
          <div className="flex -space-x-2">
            {participants.map((p, index) => {
              return <div key={index}>
                <UserAvatar
                  key={index}
                  userId={p.userId}              // ✅ ALWAYS ID
                  displayName={p.name}
                  className="h-8 w-8"
                />
              </div>;
            })}
          </div>

          <span className="ml-3 text-[#4A5565] text-sm md:text-base">
            {participants.length} Attendees
          </span>
        </div>

        {/* <div className="flex items-center gap-2 mt-2">
            {attendees.map((attendee, idx) => (
              <img
                key={idx}
                src={attendee}
                alt={`attendee-${idx}`}
                className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
              />
            ))}
            <span className="text-gray-500 text-sm">{attendees.length} attendees</span>
          </div> */}

        <div className="modal_footer mt-6 pt-4 border-t border-gray-200 dark:border-gray-700" style={{ gap: "10px" }}>
          <button className="px-4 py-1 text-xs font-medium rounded-[10px] border text-gray-600 bg-white hover:bg-gray-300" onClick={onClose}>
            Close
          </button>
          {event?.ownerUserID !== currentUser?.uid && event.isInvitationAccept == null &&
            currentTime.isBefore(moment.utc(event?.startTime)) && (
              <>
                <button className="px-4 py-1 text-xs font-medium rounded-[10px] text-[#5FBE43] bg-[#F0FFEB] hover:bg-green-200"
                  onClick={() => AcceptRejectMeetingInvitation(true)} >
                  Accept
                </button>
                <button className="px-4 py-1 text-xs font-medium rounded-[10px] text-[#FF4747] bg-[#FFEFEF] hover:bg-red-200"
                  onClick={() => AcceptRejectMeetingInvitation(false)}>
                  Reject
                </button>
              </>
            )}


          <OverlayTrigger
            placement="top"
            overlay={
              !canJoin ? (
                <Tooltip id={`tooltip-join-${event.id}`}>{tooltipMessage}</Tooltip>
              ) : (
                <></>
              )
            }
            trigger={['hover', 'focus', 'click']}
          >
            <span className="d-inline-block">
              <Button
                className="btn_flx !text-xs !rounded-[10px]"
                variant="primary"
                onClick={() => handleClickJoin(event.id, !canJoin)}
                disabled={!canJoin}
              // style={{ ...(!canJoin && { pointerEvents: 'auto' }), backgroundColor: '#7E57C2', borderColor: '#7E57C2' }}
              >
                <img src={videocall} alt="" /> Join
              </Button>
            </span>
          </OverlayTrigger>
        </div>
      </div>

      <ModalMeet isOpen={isEditModalOpen} onClose={() => setEditModalOpen(false)}>
        <EditEvent
          event={event}
          onClose={() => {
            setEditModalOpen(false);
            onClose();
          }}
          onEventCreated={refreshAllEvents}
        />
      </ModalMeet>

      {/* Add the RescheduleMeetingModal here */}
      <ModalMeet isOpen={showRescheduleModal} onClose={() => setShowRescheduleModal(false)}>
        {event && (
          <RescheduleMeetingModal
            handleCloseRescheduleModal={() => setShowRescheduleModal(false)}
            rescheduleModal={true}
            authToken={token}
            selectedMeeting={{
              id: event.id,
              title: event.title,
              startTime: event.startTime,
              endTime: event.endTime,
              description: event.description,
              ownerUserID: event.ownerUserID,
              meetingDetails: event.meetingDetails,
              location: event.location,
              participantUsers: event.participantUserIds.map(id => ({
                participantId: id,
                participantName: employees.find(emp => emp.userId === id)?.userName || id
              })),
              externalUserEmails: event.externalUserEmails,
              ownerColor: event.ownerColor,
              ownerName: event.ownerName,
              online: event.online,
              isSprout: event.isSprout
            }}
            refreshMeeting={() => {
              if (refreshAllEvents) refreshAllEvents();
              onClose();
            }}
          />
        )}
      </ModalMeet>

      {/* Scope Modal for Delete */}
      <ScopeModal
        isOpen={showDeleteScopeModal}
        onClose={() => setShowDeleteScopeModal(false)}
        onSelectScope={executeDelete}
        action="delete"
      />
    </>
  );
}
